<template>
  <div>
    <button
      class="h-8 w-8 cursor-pointer rounded-sm hover:bg-grey-hover active:bg-primary/10"
      data-cy="deal-links-button"
      :class="showDealLinks ? 'bg-primary/10 text-primary' : 'text-label'"
      @click="handleShowingLinks"
    >
      <icon-base :icon="IconLinkNoColor" view-box="0 0 22 22" />
    </button>
    <div
      v-if="hasBeenShown"
      v-show="showDealLinks"
      ref="dealLinksRef"
      v-on-click-outside="handleClose"
      class="absolute z-20 max-h-[58vh] w-[700px] overflow-y-auto rounded-sm bg-white shadow-popover"
    >
      <div>
        <deal-links :application-data="model" class="deal-links-container">
          <template #supplemental>
            <div
              class="supplemental flex w-full items-center gap-x-6 border-b py-2 text-sm text-gray-400"
            >
              <div
                v-for="(item, index) in supplementalInfo"
                :key="item.icon.toString()"
                class="inline-flex items-center gap-x-2"
              >
                <icon-base
                  :icon="item.icon"
                  data-cy="supplemental-info-item-icon"
                  :view-box="item.viewBox"
                  class="reset shrink-0 text-primary"
                />
                <div
                  ref="supplementalInfoTextRef"
                  v-tooltip="{
                    content: item.text,
                    onShow: () => isSupplementalInfoTextClamped(index),
                  }"
                  class="line-clamp-1"
                  data-cy="supplemental-info-item-text"
                >
                  {{ item.text }}
                </div>
              </div>
            </div>
          </template>
        </deal-links>
        <personal-links
          :application-data="model"
          :deal-owners="owners"
          class="personal-links-container"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import { isTextClamped } from "@/helpers/UI";
import { COMPOSITE_DROPDOWN_MODAL_ID } from "@/helpers/constants/deals";
import { useTeleportV2 } from "@/hooks/elements";

import IconCompany from "@/components/icons/IconCompany.vue";
import IconLinkNoColor from "@/components/icons/IconLinkNoColor.vue";
import IconBusinesses from "@/components/icons/sidebar/IconBusinesses.vue";
import IconWorkflowLinear from "@/components/icons/workflow/IconWorkflowLinear.vue";
import DealLinks from "@/views/deals/components/DealLinks.vue";
import PersonalLinks from "@/views/deals/components/PersonalLinks.vue";

import type { IApplication } from "@/models/applications";

const props = defineProps<{ model: IApplication }>();

const { getters } = useStore();

const dealsData = computed<IApplication[]>(() => getters["applications/all"]);

const showDealLinks = ref(false);
const hasBeenShown = ref(false);
const dealLinksRef = ref<HTMLDivElement | null>(null);
const supplementalInfoTextRef = ref<(HTMLDivElement | null)[]>([]);

const rowIndex = computed(() => {
  return dealsData.value.findIndex((deal) => deal.id === props.model.id);
});

const offset = computed(() => {
  return {
    top: rowIndex.value + 1 > dealsData.value.length / 1.4 ? -700 : -50,
    left: -700,
  };
});

const owners = computed(() => {
  const allOwners = props.model.business?.other_owners || [];
  const primary = props.model.personal_information;
  if (primary) {
    allOwners.unshift(primary);
  }
  return allOwners;
});

const supplementalInfo = computed(() => {
  return [
    {
      icon: IconBusinesses,
      viewBox: "7 7 24 24",
      text: props.model.business.business_legal_name,
    },
    {
      icon: IconCompany,
      viewBox: "0 0 48 48",
      text: props.model.client?.name || "-",
    },
    {
      icon: IconWorkflowLinear,
      viewBox: "0 0 26 26",
      text: props.model.workflow_name,
    },
  ];
});

const handleShowingLinks = () => {
  showDealLinks.value = true;
  hasBeenShown.value = true;
};

const handleClose = (event: PointerEvent) => {
  const compositeDropdownModal = document.getElementById(
    COMPOSITE_DROPDOWN_MODAL_ID
  );
  if (compositeDropdownModal?.contains(event.target as Node)) {
    return;
  }
  showDealLinks.value = false;
};

const isSupplementalInfoTextClamped = (index: number) =>
  isTextClamped(supplementalInfoTextRef.value[index]);

useTeleportV2({
  target: dealLinksRef,
  to: "#menus",
  offset: offset.value,
});
</script>

<style>
@reference "@/assets/tailwind.css";

.deal-links-container {
  container-type: inline-size;
  width: 685px;
  container-name: deal-links-container;
}

.personal-links-container {
  container-type: inline-size;
  width: 685px;
  container-name: personal-links-container;
}

@container deal-links-container (max-width: 700px) {
  .left-col {
    @apply w-3/5;
  }
  .min-w-63 {
    min-width: 150px !important;
  }
  a.flex {
    @apply shrink-0 grow;
  }
}

@container personal-links-container (max-width: 700px) {
  .left-col {
    @apply w-3/5;
  }
  svg {
    @apply flex shrink-0;
  }
  .min-w-63 {
    min-width: 150px !important;
  }
  a.flex {
    @apply shrink-0 grow;
  }
  .flex.flex-col {
    @apply shrink;
  }
}
</style>
<style scoped>
@reference "@/assets/tailwind.css";

.supplemental :deep(svg.reset) {
  margin-bottom: 0px !important;
}
</style>
